/**
 * Development logger utility
 *
 * Provides consistent logging patterns for development vs production,
 * with different log levels and contextual information.
 */

type LogContext = string

interface LogData {
  [key: string]: any
}

/**
 * Logger class for consistent application logging
 */
class Logger {
  private isDevelopment = process.env.NODE_ENV === 'development'
  private isEnabled = this.isDevelopment

  /**
   * Log debug information (development only)
   */
  debug(message: string, data?: LogData, context?: LogContext) {
    if (!this.isDevelopment) return

    const logMessage = context ? `[${context}] ${message}` : message

    if (data) {
      console.debug(logMessage, data)
    } else {
      console.debug(logMessage)
    }
  }

  /**
   * Log informational messages
   */
  info(message: string, data?: LogData, context?: LogContext) {
    if (!this.isEnabled) return

    const logMessage = context ? `[${context}] ${message}` : message

    if (data) {
      console.info(logMessage, data)
    } else {
      console.info(logMessage)
    }
  }

  /**
   * Log warning messages
   */
  warn(message: string, data?: LogData, context?: LogContext) {
    if (!this.isEnabled) return

    const logMessage = context ? `[${context}] ${message}` : message

    if (data) {
      console.warn(logMessage, data)
    } else {
      console.warn(logMessage)
    }
  }

  /**
   * Log error messages (always enabled)
   */
  error(message: string, error?: Error | LogData, context?: LogContext) {
    const logMessage = context ? `[${context}] ${message}` : message

    if (error instanceof Error) {
      console.error(logMessage, {
        message: error.message,
        stack: this.isDevelopment ? error.stack : undefined,
      })
    } else if (error) {
      console.error(logMessage, error)
    } else {
      console.error(logMessage)
    }
  }

  /**
   * Log API operations
   */
  api(operation: string, data: LogData, context: LogContext = 'API') {
    if (!this.isDevelopment) return

    console.debug(`[${context}] ${operation}`, {
      timestamp: new Date().toISOString(),
      ...data,
    })
  }

  /**
   * Log store operations
   */
  store(action: string, data: LogData, context: LogContext = 'Store') {
    if (!this.isDevelopment) return

    console.debug(`[${context}] ${action}`, {
      timestamp: new Date().toISOString(),
      ...data,
    })
  }

  /**
   * Log component lifecycle events
   */
  component(event: string, componentName: string, data?: LogData) {
    if (!this.isDevelopment) return

    console.debug(`[Component:${componentName}] ${event}`, data || {})
  }

  /**
   * Performance logging
   */
  performance(label: string, startTime: number, context?: LogContext) {
    if (!this.isDevelopment) return

    const duration = Date.now() - startTime
    const logMessage = context ? `[${context}] ${label}` : label

    console.debug(`${logMessage} completed in ${duration}ms`)
  }

  /**
   * Group related log messages
   */
  group(label: string, fn: () => void) {
    if (!this.isDevelopment) return

    console.group(label)
    try {
      fn()
    } finally {
      console.groupEnd()
    }
  }

  /**
   * Create a contextual logger
   */
  withContext(context: LogContext) {
    return {
      debug: (message: string, data?: LogData) =>
        this.debug(message, data, context),
      info: (message: string, data?: LogData) =>
        this.info(message, data, context),
      warn: (message: string, data?: LogData) =>
        this.warn(message, data, context),
      error: (message: string, error?: Error | LogData) =>
        this.error(message, error, context),
    }
  }

  /**
   * Enable/disable logging
   */
  setEnabled(enabled: boolean) {
    this.isEnabled = enabled
  }
}

// Create singleton logger instance
export const logger = new Logger()

// Export convenience functions
export const log = logger
export const createLogger = (context: LogContext) => logger.withContext(context)

// Common logger instances for different parts of the app
export const apiLogger = logger.withContext('API')
export const storeLogger = logger.withContext('Store')
export const componentLogger = (name: string) =>
  logger.withContext(`Component:${name}`)

/**
 * Performance measurement utility
 */
export const measure = (label: string, context?: LogContext) => {
  const startTime = Date.now()

  return {
    end: () => logger.performance(label, startTime, context),
  }
}

/**
 * Conditional logging based on environment
 */
export const devOnly = (fn: () => void) => {
  if (process.env.NODE_ENV === 'development') {
    fn()
  }
}

/**
 * Log function execution time
 */
export const logExecutionTime = <T extends (...args: unknown[]) => unknown>(
  fn: T,
  label: string,
  context?: LogContext
): T => {
  return ((...args: Parameters<T>) => {
    const timer = measure(label, context)
    const result = fn(...args)

    if (result instanceof Promise) {
      return result.finally(() => timer.end()) as ReturnType<T>
    } else {
      timer.end()
      return result
    }
  }) as T
}
